import { useEffect, useMemo, useState, type FormEvent, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type Product, type CompTicketsResult } from '../api.js';
import { useActiveProfileId } from '../active-profile.js';
import { Icon } from '../components/Icon.js';
import { DIAL_CODES, type DialEntry } from '../components/dial-codes.js';
import { Topbar } from './AdminLayout.js';

const ERROR_MAP: Record<string, string> = {
  ticket_number_taken: 'Номер уже занят',
  integrations_failed_check_logs: 'Билет выписан, но интеграции дали сбой — проверь логи',
  no_active_profile: 'Не выбран активный профиль',
  product_not_found: 'Продукт не найден или не принадлежит профилю',
  invalid_body: 'Проверь поля формы',
};

function friendlyError(code: string | undefined): string {
  if (!code) return 'Неизвестная ошибка';
  return ERROR_MAP[code] ?? code;
}

export function CompTicketIssue(): JSX.Element {
  const nav = useNavigate();
  const [activeProfileId] = useActiveProfileId();

  // Параметры выпуска
  const [products, setProducts] = useState<Product[]>([]);
  const [productId, setProductId] = useState<string>('');
  const [prefix, setPrefix] = useState<string>('AIS2_');
  const [padding, setPadding] = useState<number>(3);
  const [startNumber, setStartNumber] = useState<number>(1);

  // Гость — теперь только один (массового выпуска больше нет)
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dial, setDial] = useState<DialEntry>(DIAL_CODES.find((d) => d.cc === 'IL') ?? DIAL_CODES[0]!);
  const [dialOpen, setDialOpen] = useState(false);
  const [dialSearch, setDialSearch] = useState('');
  const [messengerType, setMessengerType] = useState<'telegram' | 'whatsapp'>('telegram');
  const [messengerValue, setMessengerValue] = useState('');

  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [result, setResult] = useState<CompTicketsResult | null>(null);
  const [topErr, setTopErr] = useState<string | null>(null);

  // Загрузка продуктов
  useEffect(() => {
    setLoading(true);
    api
      .listProducts()
      .then((all) => {
        const ticketed = all.filter((p) => p.ticketingEnabled);
        setProducts(ticketed);
        if (ticketed.length > 0 && !productId) {
          const first = ticketed[0]!;
          setProductId(first.id);
        }
      })
      .catch((e: Error) => setTopErr(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfileId]);

  // Подсказать следующий свободный номер
  useEffect(() => {
    if (!productId || !prefix.trim()) return;
    let alive = true;
    api
      .getCompTicketNextNumber(productId, prefix.trim())
      .then((r) => {
        if (alive) setStartNumber(r.nextNumber);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [productId, prefix]);

  const ticketPreview = useMemo(
    () => `${prefix}${String(startNumber).padStart(padding, '0')}`,
    [prefix, startNumber, padding],
  );

  const filteredDial = useMemo(() => {
    const s = dialSearch.trim().toLowerCase();
    if (!s) return DIAL_CODES;
    return DIAL_CODES.filter(
      (d) =>
        d.label.toLowerCase().includes(s) ||
        d.code.includes(s) ||
        d.cc.toLowerCase().includes(s),
    );
  }, [dialSearch]);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (submitting) return;
    setTopErr(null);
    setResult(null);

    if (!productId) {
      setTopErr('Выбери продукт');
      return;
    }
    if (!prefix.trim()) {
      setTopErr('Префикс не может быть пустым');
      return;
    }
    if (!name.trim() || !email.trim() || !phone.trim()) {
      setTopErr('Имя, email и телефон обязательны');
      return;
    }

    setSubmitting(true);
    try {
      // Полный телефон — код страны + локальная часть.
      // Бэкенд хранит как есть в Payment.customerPhone, дальше его рендерит
      // Telegram-бот и Notion.
      const fullPhone = `${dial.code} ${phone.trim()}`.trim();

      const payload = {
        productId,
        prefix: prefix.trim(),
        padding,
        startNumber,
        // Currency не показываем в UI — для бесплатного билета не имеет значения.
        currency: 'ILS' as const,
        // Один гость в массиве — backend принимает customers[], мы шлём массив из одного.
        customers: [
          {
            name: name.trim(),
            email: email.trim(),
            phone: fullPhone,
            messengerType: messengerValue.trim() ? messengerType : null,
            messengerValue: messengerValue.trim() || null,
          },
        ],
      };
      const res = await api.createCompTickets(payload);
      setResult(res);
      if (res.created === 1) {
        nav('/admin/comp-tickets');
      }
    } catch (err) {
      const e = err as Error & { details?: { error?: string } };
      const code = e.details?.error ?? e.message;
      setTopErr(friendlyError(code));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <>
        <Topbar title="Выписать билет" />
        <div style={{ padding: 24, color: 'var(--fg-subtle)' }}>Загрузка…</div>
      </>
    );
  }

  if (products.length === 0) {
    return (
      <>
        <Topbar title="Выписать билет" />
        <div style={{ padding: '24px 28px' }}>
          <div className="card" style={{ maxWidth: 640 }}>
            <div className="card-body" style={{ color: 'var(--fg-muted)' }}>
              Нет продуктов с включённым тикетингом. Включи «Тикетинг» в карточке продукта,
              чтобы можно было выписывать билеты.
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar title="Выписать билет" />

      <div style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
        <form onSubmit={onSubmit}>
          {topErr && (
            <div
              className="card"
              style={{
                marginBottom: 14,
                color: 'var(--danger-fg)',
                background: 'var(--danger-subtle-bg)',
              }}
            >
              <div className="card-body">{topErr}</div>
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 380px',
              gap: 20,
              maxWidth: 1080,
            }}
          >
            {/* ============== LEFT — Гость ============== */}
            <div className="stack">
              <div className="card">
                <div className="card-header">
                  <div className="card-title">Гость</div>
                </div>
                <div className="card-body stack">
                  <div className="grid-2">
                    <div className="field">
                      <label className="field-label">Имя и фамилия</label>
                      <input
                        type="text"
                        className="input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Эли Коэн"
                        required
                      />
                    </div>
                    <div className="field">
                      <label className="field-label">Email</label>
                      <input
                        type="email"
                        className="input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="guest@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid-2">
                  {/* Телефон с выбором кода страны */}
                  <div className="field">
                    <label className="field-label">Телефон</label>
                    <div style={{ position: 'relative' }}>
                      <div className="field-prefix">
                        <button
                          type="button"
                          onClick={() => setDialOpen((o) => !o)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '0 10px 0 12px',
                            border: 'none',
                            borderRight: '1px solid var(--hairline)',
                            background: 'var(--bg-subtle)',
                            fontSize: 13,
                            cursor: 'pointer',
                            flexShrink: 0,
                          }}
                        >
                          <span style={{ fontSize: 16, lineHeight: 1 }}>{dial.flag}</span>
                          <span className="mono">{dial.code}</span>
                          <Icon name="chevronDown" size={11} />
                        </button>
                        <input
                          className="input"
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder={
                            dial.cc === 'IL'
                              ? '50 000 0000'
                              : dial.cc === 'RU'
                                ? '900 000 00 00'
                                : 'номер телефона'
                          }
                          style={{
                            flex: 1,
                            border: 'none',
                            borderRadius: 0,
                            background: 'transparent',
                          }}
                          required
                        />
                      </div>
                      {dialOpen && (
                        <>
                          <div
                            onClick={() => {
                              setDialOpen(false);
                              setDialSearch('');
                            }}
                            style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              top: 'calc(100% + 4px)',
                              left: 0,
                              right: 0,
                              zIndex: 41,
                              background: 'var(--surface)',
                              border: '1px solid var(--border)',
                              borderRadius: 'var(--radius)',
                              boxShadow: 'var(--shadow-lg)',
                              padding: 4,
                            }}
                          >
                            <div
                              style={{
                                padding: '4px 4px 6px',
                                borderBottom: '1px solid var(--hairline)',
                              }}
                            >
                              <input
                                className="input"
                                value={dialSearch}
                                onChange={(e) => setDialSearch(e.target.value)}
                                placeholder="Поиск страны или кода…"
                                autoFocus
                                style={{ height: 34, fontSize: 13 }}
                              />
                            </div>
                            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                              {filteredDial.map((d) => (
                                <button
                                  key={d.cc}
                                  type="button"
                                  onClick={() => {
                                    setDial(d);
                                    setDialOpen(false);
                                    setDialSearch('');
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    width: '100%',
                                    padding: '6px 10px',
                                    border: 'none',
                                    background:
                                      d.cc === dial.cc ? 'var(--bg-subtle)' : 'transparent',
                                    fontSize: 13,
                                    textAlign: 'left',
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    color: d.cc === dial.cc ? 'var(--fg)' : 'var(--fg-muted)',
                                    fontWeight: d.cc === dial.cc ? 600 : 400,
                                  }}
                                >
                                  <span style={{ fontSize: 15 }}>{d.flag}</span>
                                  <span className="mono" style={{ width: 56 }}>
                                    {d.code}
                                  </span>
                                  <span style={{ flex: 1 }}>{d.label}</span>
                                  {d.cc === dial.cc && <Icon name="check" size={12} />}
                                </button>
                              ))}
                              {filteredDial.length === 0 && (
                                <div
                                  style={{
                                    padding: 12,
                                    textAlign: 'center',
                                    color: 'var(--fg-subtle)',
                                    fontSize: 13,
                                  }}
                                >
                                  Ничего не найдено
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Мессенджер — pill toggle TG/WA + input */}
                  <div className="field">
                    <label className="field-label">
                      Мессенджер{' '}
                      <span style={{ color: 'var(--fg-subtle)', fontWeight: 400 }}>
                        — не обязательно
                      </span>
                    </label>
                    <div className="field-prefix" style={{ padding: 0 }}>
                      <div
                        style={{
                          display: 'inline-flex',
                          padding: 3,
                          background: 'var(--bg-subtle)',
                          borderRight: '1px solid var(--hairline)',
                        }}
                      >
                        {(['telegram', 'whatsapp'] as const).map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setMessengerType(t)}
                            style={{
                              padding: '0 10px',
                              height: 'calc(var(--row-h) - 6px)',
                              border: 'none',
                              background:
                                messengerType === t ? 'var(--surface)' : 'transparent',
                              color:
                                messengerType === t ? 'var(--fg)' : 'var(--fg-muted)',
                              borderRadius: 6,
                              fontSize: 12.5,
                              fontWeight: 500,
                              cursor: 'pointer',
                              boxShadow:
                                messengerType === t ? 'var(--shadow-sm)' : 'none',
                            }}
                          >
                            {t === 'telegram' ? 'TG' : 'WA'}
                          </button>
                        ))}
                      </div>
                      <input
                        className="input"
                        value={messengerValue}
                        onChange={(e) => setMessengerValue(e.target.value)}
                        placeholder="@юзернем / номер"
                        style={{
                          border: 'none',
                          borderRadius: 0,
                          background: 'transparent',
                          flex: 1,
                          minWidth: 0,
                        }}
                      />
                    </div>
                  </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ============== RIGHT — Параметры + Результат ============== */}
            <div className="stack">
              <div className="card">
                <div className="card-header">
                  <div className="card-title">Параметры выпуска</div>
                </div>
                <div className="card-body stack">
                  <div className="field">
                    <label className="field-label">Продукт</label>
                    <select
                      className="input"
                      value={productId}
                      onChange={(e) => setProductId(e.target.value)}
                      required
                    >
                      {products.map((p) => (
                        <option key={p.id} value={p.id} dir="auto">
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="field">
                    <label className="field-label">Префикс билета</label>
                    <input
                      type="text"
                      className="input mono"
                      value={prefix}
                      onChange={(e) => setPrefix(e.target.value)}
                      placeholder="AIS2_"
                      required
                    />
                  </div>

                  <div className="grid-2">
                    <div className="field">
                      <label className="field-label">Номер</label>
                      <input
                        type="number"
                        className="input no-spinner"
                        min={1}
                        value={startNumber}
                        onChange={(e) => setStartNumber(parseInt(e.target.value, 10) || 1)}
                        required
                      />
                    </div>
                    <div className="field">
                      <label className="field-label">Длина</label>
                      <input
                        type="number"
                        className="input no-spinner"
                        min={1}
                        max={8}
                        value={padding}
                        onChange={(e) => setPadding(parseInt(e.target.value, 10) || 3)}
                        required
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      padding: '10px 12px',
                      background: 'var(--bg-subtle)',
                      borderRadius: 'var(--radius)',
                      fontSize: 13,
                      color: 'var(--fg-subtle)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>Будет выпущен билет</span>
                    <span
                      className="mono"
                      style={{ color: 'var(--fg)', fontWeight: 600 }}
                    >
                      {ticketPreview}
                    </span>
                  </div>

                  <hr className="hr" />

                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn btn-primary"
                    style={{ justifyContent: 'center', width: '100%' }}
                  >
                    <Icon name="check" size={14} />
                    {submitting ? 'Выпускаем…' : 'Выписать билет'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ justifyContent: 'center', width: '100%' }}
                    onClick={() => nav('/admin/comp-tickets')}
                  >
                    Отмена
                  </button>
                </div>
              </div>

              {result && result.results.length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <div className="card-title">Результат</div>
                  </div>
                  <div className="card-body">
                    {result.results.map((r) => (
                      <div
                        key={r.ticketNumber}
                        style={{
                          padding: '8px 10px',
                          borderRadius: 'var(--radius)',
                          background: r.ok
                            ? 'var(--success-subtle-bg)'
                            : 'var(--danger-subtle-bg)',
                          color: r.ok ? 'var(--success-fg)' : 'var(--danger-fg)',
                          fontSize: 13,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span
                          className="mono"
                          style={{ fontWeight: 500 }}
                        >
                          {r.ticketNumber}
                        </span>
                        <span style={{ textAlign: 'right' }}>
                          {r.ok
                            ? r.error
                              ? '⚠ ' + friendlyError(r.error)
                              : '✓ выписан'
                            : '✗ ' + friendlyError(r.error)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
